// src/pages/member/MemberUpdate.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  adminSearchUsers,
  adminGetUser,
  adminUpdateUser,
} from "../../api/admin";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon as HICheck } from "@heroicons/react/20/solid";
import { absUrl } from "../../api/client";

export default function MemberUpdate() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");

  const pages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / pageSize)),
    [total]
  );

  const fetchList = async () => {
    setListLoading(true);
    setListErr("");
    try {
      const res = await adminSearchUsers({ q, skip: (page - 1) * pageSize, limit: pageSize });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      setListErr(e?.message || "목록 조회 실패");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  // ---------- 선택/상세/수정 상태 ----------
  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [workplace, setWorkplace] = useState("");
  const [password, setPassword] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [active, setActive] = useState(true);

  const [previewUrl, setPreviewUrl] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDetail = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setDetailErr("");
    try {
      const u = await adminGetUser(id);
      setName(u.name || "");
      setPhone(u.phone || "");
      setWorkplace(u.workplace || "");
      setPassword("");
      setMarketing(!!u.consent_marketing);
      setActive(!!u.is_active);
      setPreviewUrl(u.image_url || "");
      setFile(null);
    } catch (e) {
      setDetailErr(e?.message || "회원 조회 실패");
    } finally {
      setDetailLoading(false);
    }
  };
  const WORKPLACES = ["온핏 강남점", "온핏 신촌점", "온핏 판교점"];
  const onPick = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onSearchSubmit = async (e) => {
    e.preventDefault();
    setPage(1);
    await (() => {
      // 첫 페이지로 이동 후 목록 새로고침
      setTimeout(fetchList, 0);
    })();
  };

  const onClickRow = (id) => {
    setSelectedId(id);
    loadDetail(id);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSaveMsg("");
    setSaveErr("");
    setSaving(true);
    try {
      await adminUpdateUser(selectedId, {
        name,
        phone,
        workplace,
        password: password || undefined,
        consent_marketing: marketing,
        is_active: active,
        image: file || undefined,
      });
      setSaveMsg("저장되었습니다.");
      setPassword("");
      // 저장 후 목록/상세 동기화
      await loadDetail(selectedId);
      await fetchList();
    } catch (e) {
      setSaveErr(e?.data?.detail || e.message || "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* 좌측: 검색 + 목록 */}
      <div className="space-y-4">
        <div className="rounded-xl border bg-white p-4">
          <h2 className="font-semibold mb-3 text-sm md:text-base">회원 검색</h2>
          <form onSubmit={onSearchSubmit} className="flex gap-2">
            <input
              className="flex-1 h-10 rounded-xl border px-3 text-sm md:text-base"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="이름 / 전화 / 사업장"
            />
            <button className="px-4 h-10 rounded-xl bg-blue-600 text-white text-sm md:text-base">
              검색
            </button>
          </form>
        </div>

        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-semibold text-sm md:text-base">검색 결과</div>
            <div className="text-sm text-gray-500">{total}명</div>
          </div>

          {listLoading && <div className="p-6 text-sm">불러오는 중…</div>}
          {listErr && <div className="p-6 text-sm text-red-600">{listErr}</div>}

          {!listLoading && !listErr && (
            <div className="w-full mx-auto"> {/* 테이블 블록을 화면 중앙 정렬 */}
              <table className="w-full text-[10px] md:text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="p-3 text-center">이름</th>
                    <th className="p-3 text-center">전화</th>
                    <th className="p-3 text-center">성별</th>
                    <th className="p-3 text-center">생년</th>
                    <th className="p-3 text-center">사업장</th>
                  </tr>
                </thead>

                <tbody className="text-center align-middle"> {/* 바디 전체 가운데 + 세로 중앙 */}
                  {items.map((u) => (
                    <tr
                      key={u.id}
                      className={`border-t hover:bg-gray-50 cursor-pointer ${selectedId === u.id ? "bg-blue-50" : ""
                        }`}
                      onClick={() => onClickRow(u.id)}
                    >
                      <td className="p-3">{u.name}</td>
                      <td className="p-3">{u.phone}</td>
                      <td className="p-3">{u.gender || "-"}</td>
                      <td className="p-3">{u.birthdate || "-"}</td>
                      <td className="p-3">{u.workplace || "-"}</td>
                    </tr>
                  ))}

                  {items.length === 0 && (
                    <tr>
                      <td className="p-6 text-gray-500 text-center" colSpan={6}>
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          )}

          {/* 페이지네이션 */}
          {pages > 1 && (
            <div className="p-3 flex gap-2 justify-center border-t">
              <button
                className="px-3 py-1 rounded-lg border disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                이전
              </button>
              <div className="px-2 py-1 text-sm text-gray-600">
                {page} / {pages}
              </div>
              <button
                className="px-3 py-1 rounded-lg border disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
              >
                다음
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 우측: 상세/수정 */}
      <div className="rounded-xl border bg-white p-4">
        <h2 className="font-semibold mb-3 text-sm md:text-base">회원 정보 수정</h2>

        {!selectedId && (
          <div className="text-sm md:text-base text-gray-500">
            좌측 목록에서 회원을 선택하세요.
          </div>
        )}

        {selectedId && detailLoading && (
          <div className="text-sm">불러오는 중…</div>
        )}
        {selectedId && detailErr && (
          <div className="text-sm text-red-600">{detailErr}</div>
        )}

        {selectedId && !detailLoading && !detailErr && (
          <form onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* 프로필 이미지 */}
            <div className="sm:col-span-2">
              <label className="block text-sm md:text-base font-medium mb-1">프로필 이미지</label>
              <div className="flex items-end gap-3 min-w-0">

                <img
                  src={absUrl(previewUrl) || "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="}
                  alt="preview"
                  className="h-72 w-72 rounded-xl object-cover border"
                />


                {/* 숨김 파일 입력 */}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                />

                {/* 커스텀 트리거 버튼 */}
                <button
                  type="button"
                  onClick={onPick}
                  className="text-sm md:text-base px-4 py-3 rounded-lg border border-gray-200 bg-white  text-gray-700 hover:bg-gray-50"
                >
                  파일 선택
                </button>

                {/* 파일명 표시 원하면 여기 */}
                {/* <span className="min-w-0 flex-1 truncate text-sm text-gray-600">{fileName}</span> */}
              </div>
            </div>

            {/* 이름 */}
            <div>
              <label className="block text-sm md:text-base font-medium mb-1">이름</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* 휴대폰 */}
            <div>
              <label className="block text-sm md:text-base font-medium mb-1">휴대폰 (010-1234-5678)</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {/* 비밀번호 */}
            <div>
              <label className="block text-sm md:text-base font-medium mb-1">비밀번호 (변경 시만 입력)</label>
              <input
                type="password"
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-[#F1F1F1] outline-none text-sm md:text-base"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="새 비밀번호"
              />
            </div>

            {/* 사업장 (드롭다운) */}
            <div>
              <label className="block text-sm md:text-base font-medium mb-1">사업장</label>
              <Listbox value={workplace} onChange={setWorkplace}>
                <div className="relative">
                  <Listbox.Button className="relative w-full border border-gray-200 rounded-lg bg-[#F1F1F1] px-3 pr-9 py-2.5 text-left text-sm md:text-base focus:outline-none">
                    <span className={workplace ? "text-gray-900 block truncate" : "text-gray-400 block truncate"}>
                      {workplace || "선택"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                    </span>
                  </Listbox.Button>

                  <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <Listbox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border bg-white py-1 shadow-lg focus:outline-none">
                      {WORKPLACES.map((w) => (
                        <Listbox.Option
                          key={w}
                          value={w}
                          className={({ active }) =>
                            `cursor-pointer select-none px-3 py-2 text-sm ${active ? "bg-blue-50 text-blue-700" : "text-gray-900"}`
                          }
                        >
                          {({ selected }) => (
                            <div className="flex items-center gap-2">
                              {selected ? <HICheck className="h-4 w-4 text-blue-600" /> : <span className="h-4 w-4" />}
                              <span className="truncate">{w}</span>
                            </div>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {saveErr && <p className="sm:col-span-2 text-red-600 text-sm">{saveErr}</p>}
            {saveMsg && <p className="sm:col-span-2 text-green-600 text-sm">{saveMsg}</p>}

            {/* 저장 버튼: 오른쪽 하단 */}
            <div className="sm:col-span-2 flex justify-end">
              <button
                className="min-w-[120px] px-4 py-3 rounded-xl bg-[#5F9BFF] text-white disabled:bg-[#F1F1F1] disabled:text-gray-400"
                disabled={saving}
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
